import React, { useCallback, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import {
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Pagination,
  Card,
  OverlayTrigger,
  Tooltip as BsTooltip,
  Nav,
} from 'react-bootstrap';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import {
  FaSearch,
  FaEraser,
  FaChartPie,
  FaChartLine,
  FaChartBar,
  FaCalendarAlt,
  FaExclamationCircle,
  FaFileExcel,
} from 'react-icons/fa';
import { Formik } from 'formik';
import Select from 'react-select';
import MaskedInput from 'react-text-mask';
import moment from 'moment';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Pie, Bar, Line } from 'react-chartjs-2';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import * as constants from '~/constant';
import { buscarRelatorioConferencia } from '~/Services/Service';
import { format, salvarEmExcel } from '~/Utils/Helpers';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const CORES_STATUS = {
  naoConferido: 'rgba(255, 99, 132, 0.8)',
  pendente: 'rgba(255, 206, 86, 0.8)',
  ajustado: 'rgba(54, 162, 235, 0.8)',
  conferido: 'rgba(75, 192, 192, 0.8)',
};

const STATUS_OPTIONS = [
  { value: 1, label: 'Não Conferido' },
  { value: 2, label: 'Pendente' },
  { value: 3, label: 'Conferido' },
  { value: 4, label: 'Ajustado' },
];

const validationSchema = Yup.object().shape({
  data_inicial: Yup.string()
    .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Formato de data inválido')
    .test('valid-date', 'Data inválida', value => {
      if (!value) return true;
      return moment(value, 'DD/MM/YYYY', true).isValid();
    }),
  data_final: Yup.string()
    .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Formato de data inválido')
    .test('valid-date', 'Data inválida', value => {
      if (!value) return true;
      return moment(value, 'DD/MM/YYYY', true).isValid();
    }),
});

export default function RelatorioConferencia() {
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(13);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [openDatePickerInicial, setOpenDatePickerInicial] = useState(false);
  const [openDatePickerFinal, setOpenDatePickerFinal] = useState(false);
  const [mediaPorCorretores, setMediaPorCorretores] = useState([]);
  const [mediaPorImobiliarias, setMediaPorImobiliarias] = useState([]);
  const [mediaGeral, setMediaGeral] = useState(null);
  const [totalPorStatus, setTotalPorStatus] = useState(null);
  const [abaAtiva, setAbaAtiva] = useState('tabela');

  const [exportandoExcel, setExportandoExcel] = useState(false);
  const [dadosResponse, setDadosResponse] = useState(null);
  const [formKey, setFormKey] = useState(0);

  const empreendimentos = useSelector(state => state.empreendimentos);
  const gerentesCorretores = useSelector(state => state.corretor);

  const optionsEmpreendimentos = [
    ...empreendimentos.sort.map(item => ({
      value: item.codigo,
      label: item.codigo + ' - ' + item.descricao,
    })),
  ];

  const optionsGerentes = [
    ...gerentesCorretores.listaGerentes.map(ger => ({
      value: ger.codigo,
      label: ger.nome_imobiliaria,
    })),
  ];

  const optionsCorretores = [
    ...gerentesCorretores.listaCorretores.map(cor => ({
      value: cor.codigo,
      label: cor.codigo + ' - ' + cor.nome,
    })),
  ];

  const totalPages = Math.ceil(dados.length / recordsPerPage);

  const parseDate = str => {
    if (!str || str.length < 10) return null;
    const parts = str.split('/');
    if (parts.length !== 3) return null;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  const buscarDados = useCallback(async filtro => {
    setLoading(true);

    const filtros = {
      codigo_empreendimento: filtro.codigo_empreendimento || null,
      codigo_corretor: filtro.codigo_corretor || null,
      codigo_imobiliaria: filtro.codigo_imobiliaria || null,
      data_inicial: parseDate(filtro.data_inicial),
      data_final: parseDate(filtro.data_final),
      status: filtro.status || null,
    };

    try {
      const response = await buscarRelatorioConferencia(filtros);

      if (response) {
        setDadosResponse(response);
        setDados(response.dados_excel || []);
        setMediaPorCorretores(response.media_por_corretores || []);
        setMediaPorImobiliarias(response.media_por_imobiliarias || []);
        setMediaGeral(response.media_geral || null);
        setTotalPorStatus(response.total_por_status || null);
      }
    } catch (err) {
      toast.error(err.message || 'Erro ao buscar dados');
      setDadosResponse(null);
      setDados([]);
      setMediaPorCorretores([]);
      setMediaPorImobiliarias([]);
      setMediaGeral(null);
      setTotalPorStatus(null);
    } finally {
      setLoading(false);
      setCurrentPage(1);
    }
  }, []);

  const handlePageChange = pageNumber => {
    setCurrentPage(pageNumber);
  };

  const handleSort = column => {
    let direction = 'asc';
    if (sortConfig.key === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: column, direction });
  };

  const dadosOrdenados = useMemo(() => {
    if (sortConfig.key) {
      const sorted = [...dados].sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key])
          return sortConfig.direction === 'asc' ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key])
          return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
      return sorted;
    }
    return dados;
  }, [dados, sortConfig]);

  const currentRecords = useMemo(() => {
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    return dadosOrdenados.slice(indexOfFirstRecord, indexOfLastRecord);
  }, [dadosOrdenados, currentPage, recordsPerPage]);

  const dadosPizzaTotalStatus = useMemo(() => {
    if (
      !totalPorStatus ||
      !Array.isArray(totalPorStatus) ||
      totalPorStatus.length === 0
    ) {
      return null;
    }

    const coresMap = {
      'Não Conferido': CORES_STATUS.naoConferido,
      Pendente: CORES_STATUS.pendente,
      Ajustado: CORES_STATUS.ajustado,
      Conferido: CORES_STATUS.conferido,
    };

    const labels = totalPorStatus.map(item => item.status);
    const data = totalPorStatus.map(item => item.quantidade || 0);
    const cores = totalPorStatus.map(
      item => coresMap[item.status] || 'rgba(128, 128, 128, 0.8)'
    );

    const total = data.reduce((acc, val) => acc + val, 0);

    if (total === 0) return null;

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: cores,
          borderWidth: 1,
        },
      ],
      _total: total,
    };
  }, [totalPorStatus]);

  const dadosBarrasCorretores = useMemo(() => {
    if (!mediaPorCorretores || !mediaPorCorretores.length) return null;

    const corretoresOrdenados = [...mediaPorCorretores]
      .filter(
        c =>
          c.quantidade_nao_conferido > 0 ||
          c.quantidade_pendente > 0 ||
          c.quantidade_ajustado > 0 ||
          c.quantidade_conferido > 0
      )
      .sort((a, b) => {
        const totalA =
          (a.quantidade_nao_conferido || 0) +
          (a.quantidade_pendente || 0) +
          (a.quantidade_ajustado || 0) +
          (a.quantidade_conferido || 0);
        const totalB =
          (b.quantidade_nao_conferido || 0) +
          (b.quantidade_pendente || 0) +
          (b.quantidade_ajustado || 0) +
          (b.quantidade_conferido || 0);
        return totalB - totalA;
      })
      .slice(0, 15);

    return {
      labels: corretoresOrdenados.map(c => {
        const nome = c.corretor || 'N/I';
        return nome.length > 20 ? nome.substring(0, 20) + '...' : nome;
      }),
      datasets: [
        {
          label: 'Não Conferido',
          data: corretoresOrdenados.map(c => c.quantidade_nao_conferido || 0),
          backgroundColor: CORES_STATUS.naoConferido,
        },
        {
          label: 'Pendente',
          data: corretoresOrdenados.map(c => c.quantidade_pendente || 0),
          backgroundColor: CORES_STATUS.pendente,
        },
        {
          label: 'Ajustado',
          data: corretoresOrdenados.map(c => c.quantidade_ajustado || 0),
          backgroundColor: CORES_STATUS.ajustado,
        },
        {
          label: 'Conferido',
          data: corretoresOrdenados.map(c => c.quantidade_conferido || 0),
          backgroundColor: CORES_STATUS.conferido,
        },
      ],
    };
  }, [mediaPorCorretores]);

  const dadosBarrasImobiliarias = useMemo(() => {
    if (!mediaPorImobiliarias || !mediaPorImobiliarias.length) return null;

    const imobiliariasOrdenadas = [...mediaPorImobiliarias]
      .filter(
        i =>
          i.quantidade_nao_conferido > 0 ||
          i.quantidade_pendente > 0 ||
          i.quantidade_ajustado > 0 ||
          i.quantidade_conferido > 0
      )
      .sort((a, b) => {
        const totalA =
          (a.quantidade_nao_conferido || 0) +
          (a.quantidade_pendente || 0) +
          (a.quantidade_ajustado || 0) +
          (a.quantidade_conferido || 0);
        const totalB =
          (b.quantidade_nao_conferido || 0) +
          (b.quantidade_pendente || 0) +
          (b.quantidade_ajustado || 0) +
          (b.quantidade_conferido || 0);
        return totalB - totalA;
      });

    return {
      labels: imobiliariasOrdenadas.map(i => i.imobiliaria || 'N/I'),
      datasets: [
        {
          label: 'Não Conferido',
          data: imobiliariasOrdenadas.map(i => i.quantidade_nao_conferido || 0),
          backgroundColor: CORES_STATUS.naoConferido,
        },
        {
          label: 'Pendente',
          data: imobiliariasOrdenadas.map(i => i.quantidade_pendente || 0),
          backgroundColor: CORES_STATUS.pendente,
        },
        {
          label: 'Ajustado',
          data: imobiliariasOrdenadas.map(i => i.quantidade_ajustado || 0),
          backgroundColor: CORES_STATUS.ajustado,
        },
        {
          label: 'Conferido',
          data: imobiliariasOrdenadas.map(i => i.quantidade_conferido || 0),
          backgroundColor: CORES_STATUS.conferido,
        },
      ],
    };
  }, [mediaPorImobiliarias]);

  const dadosLinhaMediaGeral = useMemo(() => {
    if (!mediaGeral) return null;

    return {
      labels: ['Não Conferido', 'Pendente', 'Ajustado', 'Conferido'],
      datasets: [
        {
          label: 'Quantidade por Etapa',
          data: [
            mediaGeral.quantidade_nao_conferido || 0,
            mediaGeral.quantidade_pendente || 0,
            mediaGeral.quantidade_ajustado || 0,
            mediaGeral.quantidade_conferido || 0,
          ],
          borderColor: 'rgba(54, 162, 235, 1)',
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          fill: true,
          tension: 0.3,
          pointBackgroundColor: [
            CORES_STATUS.naoConferido,
            CORES_STATUS.pendente,
            CORES_STATUS.ajustado,
            CORES_STATUS.conferido,
          ],
          pointBorderColor: [
            CORES_STATUS.naoConferido,
            CORES_STATUS.pendente,
            CORES_STATUS.ajustado,
            CORES_STATUS.conferido,
          ],
          pointRadius: 6,
          pointHoverRadius: 8,
        },
      ],
      _mediaGeral: mediaGeral,
    };
  }, [mediaGeral]);

  const optionsLinhaMediaGeral = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: context => {
            const index = context.dataIndex;
            const medias = [
              mediaGeral && mediaGeral.media_nao_conferido,
              mediaGeral && mediaGeral.media_pendente,
              mediaGeral && mediaGeral.media_ajustado,
              mediaGeral && mediaGeral.media_ate_conferido,
            ];
            const quantidade = context.parsed.y || 0;
            const media = medias[index] || '-';
            return [
              `Quantidade: ${quantidade.toLocaleString('pt-BR')}`,
              `Média: ${media}`,
            ];
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: value => value.toLocaleString('pt-BR'),
        },
      },
    },
  };

  const optionsPizzaTotalStatus = useMemo(() => {
    const total = (dadosPizzaTotalStatus && dadosPizzaTotalStatus._total) || 0;
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { boxWidth: 12, font: { size: 11 } },
        },
        tooltip: {
          callbacks: {
            label: context => {
              const value = context.parsed || 0;
              const perc =
                total > 0 ? ((value / total) * 100).toFixed(1) + '%' : '0%';
              return `${context.label}: ${value.toLocaleString(
                'pt-BR'
              )} (${perc})`;
            },
          },
        },
      },
    };
  }, [dadosPizzaTotalStatus]);

  const optionsBarrasCorretores = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: {
        position: 'bottom',
        labels: { boxWidth: 12, font: { size: 11 } },
      },
      tooltip: {
        callbacks: {
          label: context => {
            const value = context.parsed.x || 0;
            return `${context.dataset.label}: ${value.toLocaleString('pt-BR')}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          callback: function(value) {
            return value.toLocaleString('pt-BR');
          },
        },
      },
      y: {
        stacked: true,
        ticks: {
          font: { size: 10 },
        },
      },
    },
  };

  const exportarExcel = async () => {
    try {
      setExportandoExcel(true);
      await salvarEmExcel(
        dadosResponse,
        '/exportador/excel/relatorio/conferencia'
      );
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    } finally {
      setExportandoExcel(false);
    }
  };

  return (
    <>
      <Header title="Relatório de Conferência de Vendas" />
      <div className="relatorio-conferencia">
        <Formik
          initialValues={{
            codigo_empreendimento: '',
            codigo_imobiliaria: '',
            codigo_corretor: '',
            status: '',
            data_inicial: moment()
              .subtract(1, 'month')
              .format('DD/MM/YYYY'),
            data_final: moment().format('DD/MM/YYYY'),
          }}
          validationSchema={validationSchema}
          onSubmit={filtro => {
            buscarDados(filtro);
          }}
        >
          {({
            handleSubmit,
            setFieldValue,
            setFieldTouched,
            resetForm,
            errors,
            touched,
            values,
          }) => (
            <>
              <Form
                onSubmit={handleSubmit}
                className="container-fluid bg-white p-3 mb-3"
              >
                <Row>
                  <Col xs={12} sm={12} md={3}>
                    <Form.Group>
                      <Form.Label>Empreendimento</Form.Label>
                      <Select
                        key={`empreendimento-${formKey}`}
                        placeholder="Selecione..."
                        options={optionsEmpreendimentos}
                        onChange={selected => {
                          setFieldValue('codigo_empreendimento', selected ? selected.value : '');
                        }}
                        styles={customStyles}
                        components={{ DropdownIndicator }}
                        noOptionsMessage={() => 'Nenhuma opção'}
                        isClearable
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={12} md={3}>
                    <Form.Group>
                      <Form.Label>Imobiliária</Form.Label>
                      <Select
                        key={`imobiliaria-${formKey}`}
                        placeholder="Selecione..."
                        options={optionsGerentes}
                        onChange={selected => {
                          setFieldValue('codigo_imobiliaria', selected ? selected.value : '');
                        }}
                        styles={customStyles}
                        components={{ DropdownIndicator }}
                        noOptionsMessage={() => 'Nenhuma opção'}
                        isClearable
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={12} md={3}>
                    <Form.Group>
                      <Form.Label>Corretor</Form.Label>
                      <Select
                        key={`corretor-${formKey}`}
                        placeholder="Selecione..."
                        options={optionsCorretores}
                        onChange={selected => {
                          setFieldValue('codigo_corretor', selected ? selected.value : '');
                        }}
                        styles={customStyles}
                        components={{ DropdownIndicator }}
                        noOptionsMessage={() => 'Nenhuma opção'}
                        isClearable
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={12} md={3}>
                    <Form.Group>
                      <Form.Label>Status</Form.Label>
                      <Select
                        key={`status-${formKey}`}
                        placeholder="Selecione..."
                        options={STATUS_OPTIONS}
                        onChange={selected => {
                          setFieldValue('status', selected ? selected.value : '');
                        }}
                        styles={customStyles}
                        components={{ DropdownIndicator }}
                        noOptionsMessage={() => 'Nenhuma opção'}
                        isClearable
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={12} md={2}>
                    <Form.Group>
                      <Form.Label>Data Inicial</Form.Label>
                      <div className="position-relative">
                        <MaskedInput
                          name="data_inicial"
                          type="tel"
                          guide={false}
                          className={`form-control ${
                            errors.data_inicial && touched.data_inicial
                              ? 'is-invalid'
                              : ''
                          }`}
                          placeholder="DD/MM/AAAA"
                          mask={constants.MASK_DATE}
                          value={values.data_inicial}
                          onChange={e => {
                            setFieldValue('data_inicial', e.target.value);
                          }}
                          onBlur={() => {
                            setFieldTouched('data_inicial', true);
                          }}
                          style={{ paddingRight: '65px' }}
                        />
                        <div
                          className="position-absolute d-flex align-items-center"
                          style={{
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            gap: '5px',
                          }}
                        >
                          {errors.data_inicial && touched.data_inicial && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <BsTooltip id="tooltip-data-inicial">
                                  {errors.data_inicial}
                                </BsTooltip>
                              }
                            >
                              <FaExclamationCircle
                                className="text-danger"
                                style={{ cursor: 'pointer' }}
                              />
                            </OverlayTrigger>
                          )}
                          <FaCalendarAlt
                            className="text-secondary"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setOpenDatePickerInicial(true)}
                          />
                        </div>
                        <MuiPickersUtilsProvider
                          utils={MomentUtils}
                          locale="pt-br"
                        >
                          <DatePicker
                            open={openDatePickerInicial}
                            onOpen={() => setOpenDatePickerInicial(true)}
                            onClose={() => setOpenDatePickerInicial(false)}
                            value={
                              values.data_inicial
                                ? moment(values.data_inicial, 'DD/MM/YYYY')
                                : null
                            }
                            onChange={date => {
                              setFieldValue(
                                'data_inicial',
                                date ? date.format('DD/MM/YYYY') : ''
                              );
                              setOpenDatePickerInicial(false);
                            }}
                            format="DD/MM/YYYY"
                            TextFieldComponent={() => null}
                          />
                        </MuiPickersUtilsProvider>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={12} md={2}>
                    <Form.Group>
                      <Form.Label>Data Final</Form.Label>
                      <div className="position-relative">
                        <MaskedInput
                          name="data_final"
                          type="tel"
                          guide={false}
                          className={`form-control ${
                            errors.data_final && touched.data_final
                              ? 'is-invalid'
                              : ''
                          }`}
                          placeholder="DD/MM/AAAA"
                          mask={constants.MASK_DATE}
                          value={values.data_final}
                          onChange={e => {
                            setFieldValue('data_final', e.target.value);
                          }}
                          onBlur={() => {
                            setFieldTouched('data_final', true);
                          }}
                          style={{ paddingRight: '65px' }}
                        />
                        <div
                          className="position-absolute d-flex align-items-center"
                          style={{
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            gap: '5px',
                          }}
                        >
                          {errors.data_final && touched.data_final && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <BsTooltip id="tooltip-data-final">
                                  {errors.data_final}
                                </BsTooltip>
                              }
                            >
                              <FaExclamationCircle
                                className="text-danger"
                                style={{ cursor: 'pointer' }}
                              />
                            </OverlayTrigger>
                          )}
                          <FaCalendarAlt
                            className="text-secondary"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setOpenDatePickerFinal(true)}
                          />
                        </div>
                        <MuiPickersUtilsProvider
                          utils={MomentUtils}
                          locale="pt-br"
                        >
                          <DatePicker
                            open={openDatePickerFinal}
                            onOpen={() => setOpenDatePickerFinal(true)}
                            onClose={() => setOpenDatePickerFinal(false)}
                            value={
                              values.data_final
                                ? moment(values.data_final, 'DD/MM/YYYY')
                                : null
                            }
                            onChange={date => {
                              setFieldValue(
                                'data_final',
                                date ? date.format('DD/MM/YYYY') : ''
                              );
                              setOpenDatePickerFinal(false);
                            }}
                            format="DD/MM/YYYY"
                            TextFieldComponent={() => null}
                          />
                        </MuiPickersUtilsProvider>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end align-items-center mt-3">
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      resetForm();
                      setFormKey(prev => prev + 1);
                    }}
                    className="mr-2"
                    type="button"
                  >
                    <FaEraser className="mr-1" /> Limpar
                  </Button>
                  <Button
                    type="submit"
                    variant="success"
                    disabled={
                      loading ||
                      values.data_inicial === '' ||
                      values.data_final === '' ||
                      (errors.data_inicial && touched.data_inicial) ||
                      (errors.data_final && touched.data_final)
                    }
                  >
                    {loading ? (
                      <Spinner animation="border" size="sm" className="mr-1" />
                    ) : (
                      <FaSearch className="mr-1" />
                    )}
                    Buscar
                  </Button>
                </div>
              </Form>

              {dados.length > 0 ? (
                <>
                  <div className="container-fluid bg-white p-3 mt-3">
                    <Row className="align-items-center mb-3">
                      <Col xs={12} className="d-flex justify-content-center">
                        <Nav
                          variant="pills"
                          activeKey={abaAtiva}
                          onSelect={k => setAbaAtiva(k)}
                          className="nav-justified"
                          style={{ minWidth: '300px' }}
                        >
                          <Nav.Item>
                            <Nav.Link
                              eventKey="tabela"
                              className="text-center"
                              style={{ fontWeight: '500' }}
                            >
                              <FaChartLine className="mr-1" /> Geral
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link
                              eventKey="graficos"
                              className="text-center"
                              style={{ fontWeight: '500' }}
                            >
                              <FaChartBar className="mr-1" /> Gráficos
                            </Nav.Link>
                          </Nav.Item>
                        </Nav>
                      </Col>
                    </Row>

                    {abaAtiva === 'graficos' && (
                      <>
                        <Row>
                          <Col xs={12} md={4}>
                            <Card className="mb-3 h-100">
                              <Card.Header className="bg-primary text-white">
                                <FaChartPie className="mr-2" />
                                Total por Status
                              </Card.Header>
                              <Card.Body style={{ height: '320px' }}>
                                {dadosPizzaTotalStatus ? (
                                  <Pie
                                    data={dadosPizzaTotalStatus}
                                    options={optionsPizzaTotalStatus}
                                  />
                                ) : (
                                  <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                    Sem dados para exibir
                                  </div>
                                )}
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col xs={12} md={8}>
                            <Card className="mb-3 h-100">
                              <Card.Header className="bg-primary text-white">
                                <FaChartLine className="mr-2" />
                                Fluxo Geral de Conferência
                              </Card.Header>
                              <Card.Body style={{ height: '320px' }}>
                                {dadosLinhaMediaGeral ? (
                                  <Line
                                    data={dadosLinhaMediaGeral}
                                    options={optionsLinhaMediaGeral}
                                  />
                                ) : (
                                  <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                    Sem dados para exibir
                                  </div>
                                )}
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>

                        <Row className="mt-2">
                          <Col xs={12} md={6}>
                            <Card className="mb-3">
                              <Card.Header className="bg-primary text-white">
                                <FaChartBar className="mr-2" />
                                Por Status (Top 15 Corretores)
                              </Card.Header>
                              <Card.Body style={{ height: '400px' }}>
                                {dadosBarrasCorretores ? (
                                  <Bar
                                    data={dadosBarrasCorretores}
                                    options={optionsBarrasCorretores}
                                  />
                                ) : (
                                  <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                    Sem dados para exibir
                                  </div>
                                )}
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col xs={12} md={6}>
                            <Card className="mb-3">
                              <Card.Header className="bg-primary text-white">
                                <FaChartBar className="mr-2" />
                                Por Status (Imobiliárias)
                              </Card.Header>
                              <Card.Body style={{ height: '400px' }}>
                                {dadosBarrasImobiliarias ? (
                                  <Bar
                                    data={dadosBarrasImobiliarias}
                                    options={optionsBarrasCorretores}
                                  />
                                ) : (
                                  <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                    Sem dados para exibir
                                  </div>
                                )}
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                      </>
                    )}

                    {abaAtiva === 'tabela' && (
                      <>
                        <Row className="d-flex justify-content-between align-items-center mb-3">
                          <Col xs="auto">
                            <span>
                              Total: {dados.length} registro(s) encontrado(s)
                            </span>
                          </Col>
                          <Col xs="auto">
                            <Button
                              variant="success"
                              size="sm"
                              title="Exportar para Excel"
                              onClick={exportarExcel}
                              disabled={exportandoExcel || !dadosResponse}
                            >
                              {exportandoExcel ? (
                                <>
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    className="mr-1"
                                  />
                                  Exportando...
                                </>
                              ) : (
                                <>
                                  <FaFileExcel className="mr-1" /> Exportar
                                  Excel
                                </>
                              )}
                            </Button>
                          </Col>
                        </Row>
                        <div className="table-responsive">
                          <Table
                            className="table table-sm table-striped table-bordered table-hover"
                            style={{ fontSize: '11px' }}
                          >
                            <Thead>
                              <Tr className="text-center">
                                <Th
                                  onClick={() => handleSort('preVenda')}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Pré-venda
                                </Th>
                                <Th
                                  onClick={() => handleSort('empreendimento')}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Empreendimento
                                </Th>
                                <Th
                                  onClick={() => handleSort('quadra')}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Quadra
                                </Th>
                                <Th
                                  onClick={() => handleSort('lote')}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Lote
                                </Th>
                                <Th
                                  onClick={() => handleSort('dataVenda')}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Data Venda
                                </Th>
                                <Th
                                  onClick={() => handleSort('dataEfetivacao')}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Data Efetivação
                                </Th>
                                <Th
                                  onClick={() => handleSort('corretor')}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Corretor
                                </Th>
                                <Th
                                  onClick={() => handleSort('imobiliaria')}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Imobiliária
                                </Th>
                                <Th
                                  onClick={() => handleSort('dataNaoConferido')}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Dt. Não Conf.
                                </Th>
                                <Th
                                  onClick={() => handleSort('dataPendente')}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Dt. Pendente
                                </Th>
                                <Th
                                  onClick={() => handleSort('dataAjustado')}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Dt. Ajustado
                                </Th>
                                <Th
                                  onClick={() => handleSort('dataConferido')}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Dt. Conferido
                                </Th>
                                <Th
                                  onClick={() =>
                                    handleSort('tempoTotalMinutos')
                                  }
                                  style={{ cursor: 'pointer' }}
                                >
                                  Tempo Total
                                </Th>
                              </Tr>
                            </Thead>
                            <Tbody className="text-center">
                              {currentRecords.map((item, index) => (
                                <Tr key={index}>
                                  <Td className="text-center">
                                    {item.codigo_prevenda}
                                  </Td>
                                  <Td className="text-left">
                                    {item.empreendimento || '-'}
                                  </Td>
                                  <Td className="text-center">
                                    {item.quadra || '-'}
                                  </Td>
                                  <Td className="text-center">
                                    {item.lote || '-'}
                                  </Td>
                                  <Td className="text-center">
                                    {format.date(item.data_venda)}
                                  </Td>
                                  <Td className="text-center">
                                    {item.data_efetivacao
                                      ? format.date(item.data_efetivacao)
                                      : '-'}
                                  </Td>
                                  <Td className="text-left">
                                    {item.corretor || '-'}
                                  </Td>
                                  <Td className="text-left">
                                    {item.imobiliaria || '-'}
                                  </Td>
                                  <Td className="text-center">
                                    {item.data_nao_conferido
                                      ? format.datetime(item.data_nao_conferido)
                                      : '-'}
                                  </Td>
                                  <Td className="text-center">
                                    {item.data_pendente
                                      ? format.datetime(item.data_pendente)
                                      : '-'}
                                  </Td>
                                  <Td className="text-center">
                                    {item.data_ajustado
                                      ? format.datetime(item.data_ajustado)
                                      : '-'}
                                  </Td>
                                  <Td className="text-center">
                                    {item.data_conferido
                                      ? format.datetime(item.data_conferido)
                                      : '-'}
                                  </Td>
                                  <Td className="text-center">
                                    {item.tempo_total_ate_conferencia}
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </div>

                        <Row className="mt-3 d-flex justify-content-between align-items-center">
                          <Col xs="auto">
                            {totalPages > 1 && (
                              <Pagination className="mb-0">
                                <Pagination.First
                                  onClick={() => handlePageChange(1)}
                                  disabled={currentPage === 1}
                                />
                                <Pagination.Prev
                                  onClick={() =>
                                    handlePageChange(currentPage - 1)
                                  }
                                  disabled={currentPage === 1}
                                />
                                {totalPages > 10 && currentPage > 5 && (
                                  <Pagination.Ellipsis className="ellipsis" />
                                )}
                                {[...Array(totalPages).keys()]
                                  .slice(
                                    Math.max(0, currentPage - 2),
                                    Math.min(totalPages, currentPage + 1)
                                  )
                                  .map(number => (
                                    <Pagination.Item
                                      key={number}
                                      active={currentPage === number + 1}
                                      onClick={() =>
                                        handlePageChange(number + 1)
                                      }
                                    >
                                      {number + 1}
                                    </Pagination.Item>
                                  ))}
                                {totalPages > 10 &&
                                  currentPage < totalPages - 4 && (
                                    <Pagination.Ellipsis className="ellipsis" />
                                  )}
                                <Pagination.Next
                                  onClick={() =>
                                    handlePageChange(currentPage + 1)
                                  }
                                  disabled={currentPage === totalPages}
                                />
                                <Pagination.Last
                                  onClick={() => handlePageChange(totalPages)}
                                  disabled={currentPage === totalPages}
                                />
                              </Pagination>
                            )}
                          </Col>
                        </Row>
                      </>
                    )}
                  </div>
                </>
              ) : (
                !loading && (
                  <div className="container-fluid bg-white p-5 mt-3 text-center">
                    <Row>
                      <Col>
                        <FaExclamationCircle
                          size={48}
                          className="text-muted mb-3"
                        />
                        <h5 className="text-muted">
                          Nenhum registro encontrado
                        </h5>
                        <p className="text-muted">
                          Utilize os filtros acima para buscar dados.
                        </p>
                      </Col>
                    </Row>
                  </div>
                )
              )}
            </>
          )}
        </Formik>
      </div>
    </>
  );
}
